/**
 * Internal Transport Service
 *
 * Service for handling internal warehouse logistics and AI pathfinding.
 * Maps to the backend TMS `DynamicRouter`.
 *
 * Capabilities:
 * - Blockage Management (Reporting spills/obstacles)
 * - Path Visualization (Retrieving node graph)
 * - Dynamic Routing (Getting A* paths with congestion)
 *
 * @module services/internalTransportService
 */

import { api } from './api';

const BASE_URL = '/api/v1/tms/internal-transport';

export const internalTransportService = {
  /**
   * Get the warehouse navigation graph (nodes and edges)
   * @param {string} warehouseId
   * @returns {Promise<Object>} Graph representation containing nodes and edges
   */
  async getWarehouseGraph(warehouseId) {
    const response = await api.get(`${BASE_URL}/graph/${warehouseId}`);
    return response.data;
  },

  /**
   * Report a temporary blockage (e.g., aisle spill, maintenance)
   * @param {Object} blockageData
   * @param {string} blockageData.source_node - Start node of blocked edge
   * @param {string} blockageData.target_node - End node of blocked edge
   * @param {string} blockageData.reason
   * @param {number} blockageData.duration_minutes - Estimated duration
   * @returns {Promise<Object>} Created blockage event
   */
  async reportBlockage(blockageData) {
    const response = await api.post(`${BASE_URL}/blockages`, blockageData);
    return response.data;
  },

  /**
   * Get active blockages for a warehouse
   * @param {string} warehouseId
   * @returns {Promise<Array>} List of active blockages
   */
  async getActiveBlockages(warehouseId) {
    const response = await api.get(`${BASE_URL}/blockages`, { params: { warehouse_id: warehouseId } });
    return response.data;
  },

  /**
   * Calculate an internal path between two points
   * @param {string} startNode
   * @param {string} endNode
   * @param {string} warehouseId
   * @returns {Promise<Object>} Optimized path with alternative suggestions
   */
  async calculatePath(startNode, endNode, warehouseId) {
    const response = await api.post(`${BASE_URL}/pathfinding/calculate`, {
      start_node: startNode,
      end_node: endNode,
      warehouse_id: warehouseId
    });
    return response.data;
  },

  /**
   * Clear a blockage
   * @param {string} blockageId
   * @returns {Promise<void>}
   */
  async clearBlockage(blockageId) {
    await api.delete(`${BASE_URL}/blockages/${blockageId}`);
  }
};

export default internalTransportService;
